<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { CONTACT_ROW_GRID } from 'dashboard/components-next/Contacts/ContactsCard/constants';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  companyId: { type: [Number, String], default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  lastActivityAt: { type: [String, Number], default: null },
  createdAt: { type: [String, Number], default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'showContact',
  'select',
  'avatarHover',
]);

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  companyId: props.companyId,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const formattedLastActivityAt = computed(() =>
  props.lastActivityAt ? dynamicTime(props.lastActivityAt) : ''
);

const formattedCreatedAt = computed(() =>
  props.createdAt ? dynamicTime(props.createdAt) : ''
);

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};
</script>

<template>
  <div
    class="w-full transition-colors"
    :class="isSelected ? 'bg-n-violet-3/60' : 'hover:bg-n-alpha-1'"
  >
    <div class="px-4 py-3" :class="CONTACT_ROW_GRID">
      <div class="flex items-center min-w-0 gap-2.5">
        <div
          class="shrink-0"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="32"
            :status="availabilityStatus"
            hide-offline-status
          >
            <template v-if="selectable" #overlay="{ size }">
              <label
                class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px] border border-n-weak"
                :style="{ width: `${size}px`, height: `${size}px` }"
                @click.stop
              >
                <Checkbox
                  :model-value="isSelected"
                  @change="event => toggleSelect(event.target.checked)"
                />
              </label>
            </template>
          </Avatar>
        </div>
        <div class="flex flex-col min-w-0">
          <button
            type="button"
            class="text-sm font-medium text-left truncate text-n-slate-12 hover:text-n-violet-11"
            :title="name"
            @click="onClickViewDetails"
          >
            {{ name }}
          </button>
          <span
            v-if="email"
            class="text-xs truncate text-n-slate-10"
            :title="email"
          >
            {{ email }}
          </span>
        </div>
      </div>

      <span class="text-sm truncate text-n-slate-11">
        {{ phoneNumber || '—' }}
      </span>

      <span
        class="text-sm truncate text-n-slate-11"
        :title="additionalAttributes?.companyName"
      >
        {{ additionalAttributes?.companyName || '—' }}
      </span>

      <span
        class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
      >
        <Flag
          v-if="countryDetails"
          :country="countryDetails.countryCode"
          class="size-3.5"
        />
        <span class="truncate">{{ formattedLocation || '—' }}</span>
      </span>

      <span class="text-sm truncate text-n-slate-11">
        {{ formattedLastActivityAt || '—' }}
      </span>

      <span class="text-sm truncate text-n-slate-11">
        {{ formattedCreatedAt || '—' }}
      </span>

      <Button
        icon="i-lucide-chevron-down"
        variant="ghost"
        color="slate"
        size="xs"
        :aria-expanded="isExpanded"
        :aria-label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE')"
        :class="{ 'rotate-180': isExpanded }"
        @click="onClickExpand"
      />
    </div>

    <div
      class="transition-all duration-500 ease-in-out grid overflow-hidden"
      :class="
        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      "
    >
      <div class="overflow-hidden">
        <div class="flex flex-col gap-6 p-6 border-t border-n-weak">
          <ContactsForm
            ref="contactsFormRef"
            :contact-data="contactData"
            @update="handleFormUpdate"
          />
          <div>
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
              size="sm"
              :is-loading="isUpdating"
              :disabled="isUpdating || isFormInvalid"
              @click="handleUpdateContact"
            />
          </div>
        </div>
        <ContactDeleteSection
          :selected-contact="{
            id: props.id,
            name: props.name,
          }"
        />
      </div>
    </div>
  </div>
</template>
